import { Close, CloseOutlined, FilterAltOffOutlined, FilterAltOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    useTheme,
} from "@mui/material";
import { FormEvent } from "react";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";

export const initialIntermediateFilter = {
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    property: "",
    meterNo: "",
    billingMonth: "",
    month: "",
    year: "",
    completed: "",
    meterStatus: "active",
    showDuplicates: false,
    wrongReadingFlag: undefined,
    connectionType: [],
    selectedColumn: "",
    selectedOrder: "",
    consumedUnits: "",
    operator: "",
};

export const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    property: false,
    operator: false,
    connectionType: false,
};

type ReadingFilterFormProps = {
    intermediateFilter: any;
    setIntermediateFilter: (filter: any) => void;
    billingMonth: string;
    setBillingMonth: (value: string) => void;
    clearAll: typeof initialClearAll;
    handleClearAll: () => void;
    handleSubmit: (ev: FormEvent<HTMLFormElement>) => void;
    isLoading: boolean;
};

export const ReadingFilterForm = ({
    intermediateFilter,
    setIntermediateFilter,
    billingMonth,
    setBillingMonth,
    clearAll,
    handleClearAll,
    handleSubmit,
    isLoading,
}: ReadingFilterFormProps) => {
    const theme = useTheme();
    const currentMonth = new Date().toISOString().slice(0, 7);

    return (
        <Grid
            container
            spacing={1.5}
            component="form"
            onSubmit={handleSubmit}
            mb={2}
        >
            <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="District"
                        api="/region/district"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        clearAll={clearAll.district}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="Powerstation"
                        api="/powerstation"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        dep={intermediateFilter.district}
                        params={{
                            district: intermediateFilter.district,
                        }}
                        clearAll={clearAll.powerstation}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="Village"
                        api="/region/village"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        dep={[
                            intermediateFilter.district,
                            intermediateFilter.powerstation,
                        ]}
                        params={{
                            district: intermediateFilter.district,
                            powerstation: intermediateFilter.powerstation,
                        }}
                        clearAll={clearAll.village}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="Hamlet"
                        api="/region/hamlet"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        dep={[
                            intermediateFilter.village,
                            intermediateFilter.powerstation,
                        ]}
                        params={{
                            village: intermediateFilter.village,
                            powerstation: intermediateFilter.powerstation,
                        }}
                        multiple={false}
                        clearAll={clearAll.hamlet}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ METER"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <TextField
                        size="small"
                        name="meterNo"
                        label="Meter no."
                        fullWidth
                        value={intermediateFilter.meterNo}
                        onChange={(e) =>
                            setIntermediateFilter({
                                ...intermediateFilter,
                                meterNo: e.target.value,
                            })
                        }
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <Grid item xs={12} md={6} lg={3}>
                <TextField
                    size="small"
                    name="billingMonth"
                    label="Billing month"
                    type="month"
                    fullWidth
                    value={billingMonth}
                    onChange={(e) => setBillingMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        max: currentMonth,
                    }}
                    InputProps={{
                        endAdornment: billingMonth ? (
                            <IconButton
                                size="small"
                                onClick={() => setBillingMonth("")}
                            >
                                <CloseOutlined />
                            </IconButton>
                        ) : null,
                    }}
                />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
                <TextField
                    select
                    size="small"
                    label="Reading Status"
                    fullWidth
                    value={intermediateFilter?.completed ?? ""}
                    InputProps={
                        intermediateFilter.completed
                            ? {
                                  endAdornment: (
                                      <IconButton
                                          size="small"
                                          sx={{ mr: theme.spacing(2) }}
                                          onClick={() =>
                                              setIntermediateFilter({
                                                  ...intermediateFilter,
                                                  completed: "",
                                              })
                                          }
                                      >
                                          <Close fontSize="small" />
                                      </IconButton>
                                  ),
                              }
                            : undefined
                    }
                    onChange={(e) =>
                        setIntermediateFilter({
                            ...intermediateFilter,
                            completed: e.target.value,
                        })
                    }
                >
                    <MenuItem value="1">Completed</MenuItem>
                    <MenuItem value="0">Pending</MenuItem>
                </TextField>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
                <TextField
                    select
                    size="small"
                    label="Meter Status"
                    fullWidth
                    value={intermediateFilter?.meterStatus ?? ""}
                    InputProps={
                        intermediateFilter.meterStatus
                            ? {
                                  endAdornment: (
                                      <IconButton
                                          size="small"
                                          sx={{ mr: theme.spacing(2) }}
                                          onClick={() =>
                                              setIntermediateFilter({
                                                  ...intermediateFilter,
                                                  meterStatus: "",
                                              })
                                          }
                                      >
                                          <Close fontSize="small" />
                                      </IconButton>
                                  ),
                              }
                            : undefined
                    }
                    onChange={(e) =>
                        setIntermediateFilter({
                            ...intermediateFilter,
                            meterStatus: e.target.value
                                ? e.target.value
                                : "active",
                        })
                    }
                >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">InActive</MenuItem>
                </TextField>
            </Grid>

            <MultiPermissionAuthorize ops={["READ PROPERTY"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="Property"
                        api="/customer/property"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        dep={[
                            intermediateFilter?.district,
                            intermediateFilter?.village,
                            intermediateFilter?.hamlet,
                        ]}
                        params={Object.fromEntries(
                            Object.entries(intermediateFilter).filter(
                                ([k, v]) =>
                                    ["district", "village", "hamlet"].includes(
                                        k
                                    ) &&
                                    v &&
                                    v !== "all"
                            )
                        )}
                        clearAll={clearAll.property}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        key={intermediateFilter.connectionType}
                        label="Connection Type"
                        api="/connection"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        params={{
                            district: intermediateFilter.district,
                        }}
                        multiple={true}
                        clearAll={clearAll.connectionType}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <MultiPermissionAuthorize ops={["READ USER"]}>
                <Grid item xs={12} md={6} lg={3}>
                    <UpdatedSearchableInput
                        label="Operator"
                        api="/user"
                        filter={intermediateFilter}
                        setFilter={setIntermediateFilter}
                        clearAll={clearAll.operator}
                    />
                </Grid>
            </MultiPermissionAuthorize>

            <Grid item xs={12} md={6} lg={3}>
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.action.disabled}`,
                        borderRadius: theme.spacing(0.5),
                        px: 1.5,
                    }}
                >
                    <FormControlLabel
                        label="Show Duplicates"
                        control={
                            <Checkbox
                                checked={Boolean(
                                    intermediateFilter.showDuplicates
                                )}
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        showDuplicates: e.target.checked
                                            ? true
                                            : false,
                                    })
                                }
                            />
                        }
                    />
                </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.action.disabled}`,
                        borderRadius: theme.spacing(0.5),
                        px: 1.5,
                    }}
                >
                    <FormControlLabel
                        label="Show Wrong Reading"
                        control={
                            <Checkbox
                                checked={Boolean(
                                    intermediateFilter.wrongReadingFlag
                                )}
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        wrongReadingFlag: e.target.checked
                                            ? true
                                            : false,
                                    })
                                }
                            />
                        }
                    />
                </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
                <TextField
                    size="small"
                    name="consumedUnits"
                    label="Consumed Units"
                    fullWidth
                    value={intermediateFilter.consumedUnits}
                    onChange={(e) =>
                        setIntermediateFilter({
                            ...intermediateFilter,
                            consumedUnits: e.target.value,
                        })
                    }
                />
            </Grid>

            <Grid item xs={12} md={6} lg={1.4}>
                <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    sx={{ height: "100%" }}
                    type="submit"
                    startIcon={
                        isLoading ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <FilterAltOutlined />
                        )
                    }
                >
                    Search
                </Button>
            </Grid>

            <Grid item xs={12} md={6} lg={1.4}>
                <Button
                    color="error"
                    variant="outlined"
                    startIcon={<FilterAltOffOutlined />}
                    onClick={handleClearAll}
                    sx={{
                        minWidth: "20ch",
                        height: "100%",
                    }}
                    fullWidth
                >
                    Clear All
                </Button>
            </Grid>
        </Grid>
    );
};
